/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const navItems = [
  { to: '/operational-memory', icon: 'hub', label: 'Operational Memory' },
  { to: '/admin-ui', icon: 'dashboard', label: 'Dashboard' },
  { to: '/lpo-form/voice', icon: 'mic', label: 'Voice Capture', filled: true },
  { to: '/secure/my-insights', icon: 'history', label: 'History' },
  { to: '/admin/artifacts', icon: 'folder_managed', label: 'Evidence Vault' },
  { to: '/admin/billing', icon: 'settings', label: 'Settings' },
];

export default function Sidebar({ storageUsed = 12.4, storageTotal = 20 }) {
  const location = useLocation();
  const storagePercent = Math.round((storageUsed / storageTotal) * 100);

  return (
    <aside className="w-64 flex-shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col justify-between py-6">
      <div className="flex flex-col gap-8">
        {/* Logo */}
        <div className="px-6 flex items-center gap-3">
          <div className="size-10 rounded-lg bg-primary flex items-center justify-center text-white shadow-lg shadow-primary/20">
            <span className="material-symbols-outlined">shield_lock</span>
          </div>
          <div className="flex flex-col">
            <h1 className="text-slate-900 dark:text-white text-lg font-bold leading-none">Fluxonix</h1>
            <p className="text-slate-500 text-xs font-medium">Enterprise Security</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1 px-3">
          {navItems.map((item) => {
            const isActive = location.pathname === item.to ||
              (item.to === '/lpo-form/voice' && location.pathname.startsWith('/lpo-form'));

            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <span
                  className="material-symbols-outlined text-xl"
                  style={item.filled && isActive ? { fontVariationSettings: "'FILL' 1" } : undefined}
                >
                  {item.icon}
                </span>
                <span className="text-sm font-semibold">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Storage Usage */}
      <div className="px-6">
        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Storage Usage</p>
          <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full mb-2">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${storagePercent}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 font-medium leading-tight">
            {storageUsed} GB of {storageTotal} GB used
          </p>
        </div>
      </div>
    </aside>
  );
}
